import { cx, dateFormat, dateDiff, dateTimeFormat } from '@/utils/strings'
import ImageContentful from '@/components/ui/image-contentful'
import LinkComponent from '@/components/link-component'
import { getExpandingBorderStyle } from '@/components/expanding-border-card'
import CtaLink, { buttonStyles } from '@/components/ui/cta-link'
import { useTranslations } from '@/utils/locale'

export type ArticleCardProps = {
  description?: string
  imageContentful?: any
  image: any
  title: string
  publishDate?: string
  updateDate?: string
  url: string
  cta?: string
  eventDate?: any
  tags?: string[]
  newsfeed?: boolean
  eyebrowText?: string
  attribution?: {
    company: string
    authors?: string[]
  }
}

export const renderTags = (tags: any) => {
  if (!tags || !Array.isArray(tags) || tags.length === 0) return null
  return (
    <div className="mb-4">
      {(tags as unknown as string[]).map((tag, index, array) => (
        <span key={tag} className="text-primaryBlue text-eyebrow inline-block">
          {tag}
          {index < array.length - 1 && (
            <span className="text-[10px] text-secondary-800 dark:text-secondary-50 mx-2">▪</span>
          )}
        </span>
      ))}
    </div>
  )
}

const renderImageContent = (imageData: any, isImageContentfulComponent: boolean, darkModeImage?: any) => {
  if (isImageContentfulComponent) {
    return <ImageContentful darkModeImage={darkModeImage} {...imageData} />
  }
  return <ImageContentful {...imageData} />
}

const getImageContainerClasses = (imageFile: any) => {
  const width = imageFile?.file?.details?.image?.width
  const height = imageFile?.file?.details?.image?.height
  const isSquareOrPortrait = width && height && width / height <= 1.4

  return cx(
    'w-full overflow-hidden aspect-video justify-center items-center flex [&_svg]:h-full [&_svg]:w-auto @4xl:rounded-lg',
    isSquareOrPortrait
      ? '[&>div:first-child]:w-auto! [&>div:first-child]:h-full! [&>div:first-child_img]:h-full! [&>div:first-child_img]:w-auto! p-5'
      : '@4xl:[&_img]:rounded @4xl:justify-start @4xl:[&>div:first-child]:h-full!',
    imageFile?.file?.contentType === 'image/svg+xml' && 'bg-primaryBlue overflow-hidden'
  )
}

export default function ArticleListCard({
  description,
  imageContentful,
  image,
  title,
  publishDate,
  updateDate,
  eventDate,
  url,
  cta,
  tags = [],
  newsfeed = false,
  eyebrowText,
  attribution,
}: ArticleCardProps) {
  const renderEventDate = (event: string) => {
    return (
      <>
        {new Date(event) > new Date() ? (
          <span>{dateFormat(event.toString(), 'en-US')}</span>
        ) : (
          <span>
            {dateTimeFormat(event.toString(), 'en-US')} &nbsp; {dateDiff(event.toString(), 'en-US')}
          </span>
        )}
      </>
    )
  }

  const { t } = useTranslations()
  const editorialPublishDate = updateDate || publishDate
  const publishOrUpdate = updateDate ? t('updated') : t('published')
  const imageContenfulFile = { ...imageContentful?.fields?.image?.fields }

  return (
    <LinkComponent
      to={`${url}`}
      className={cx(
        'block @container ',
        getExpandingBorderStyle({ borderSize: 'sm', zIndex: -1 }),
        'before:duration-175!'
      )}
    >
      <article
        className={cx(
          'rounded-lg overflow-hidden text-left flex flex-col @4xl:flex-row h-full bg-bw-gray-teal dark:bg-secondary-800'
        )}
      >
        <div className={cx(newsfeed ? 'bg-white @4xl:w-2/12 p-6' : ' @4xl:w-1/2 @4xl:p-5')}>
          <div className="flex h-full">
            {/* image component (light/dark etc.) */}
            {imageContentful && (
              <div className={getImageContainerClasses(imageContenfulFile)}>
                {renderImageContent(
                  imageContentful.fields.image.fields,
                  true,
                  imageContentful?.fields?.imageDarkMode?.fields
                )}
              </div>
            )}
            {/* non image component */}
            {!imageContentful && image && image?.fields?.file?.contentType !== 'image/svg+xml' && (
              <div className={getImageContainerClasses(image?.fields)}>{renderImageContent(image?.fields, false)}</div>
            )}
            {/* missing image */}
            {!imageContentful && !image && (
              <div className="aspect-video h-full w-full rounded-t-lg @4xl:rounded-l-lg @4xl:rounded-t-none text-center flex items-center justify-center">
                <span className="bwi bwi-bitwarden-shield text-[160px] text-primaryBlue" />
              </div>
            )}
            {/* non image component and is SVG */}
            {!imageContentful && image?.fields?.file?.contentType === 'image/svg+xml' && (
              <div
                className={cx(
                  'flex w-full aspect-video overflow-hidden items-center',
                  newsfeed
                    ? 'dark:bg-white @4xl:items-start'
                    : 'bg-primaryBlue rounded-t-lg @4xl:rounded-l-lg @4xl:rounded-t-none  '
                )}
              >
                <ImageContentful
                  className={cx(
                    '[&_svg]:text-transparent [&_svg]:m-auto [&_svg]:h-auto [&_svg]:w-1/2 @4xl:[&_svg]:w-full'
                  )}
                  {...image?.fields}
                />
              </div>
            )}
          </div>
        </div>
        <div
          className={cx(
            'h-full @4xl:h-auto p-6 relative  dark:bg-secondary-800 text-secondary-800 dark:text-secondary-50',
            cta && 'pb-16',
            newsfeed ? '@4xl:w-10/12' : '@4xl:w-1/2'
          )}
        >
          {(eyebrowText || eventDate || editorialPublishDate) && (
            <p className="text-sm mb-2 uppercase text-secondary-600 dark:text-secondary-200">
              {eyebrowText && <>{eyebrowText}</>}
              {eventDate && <time>{renderEventDate(eventDate)}</time>}
              {editorialPublishDate && (
                <time>{`${publishOrUpdate}:  ${dateFormat(editorialPublishDate, 'en-US')}`}</time>
              )}
            </p>
          )}

          {tags && renderTags(tags)}

          {title && <h2 className={cx('text-2xl', !attribution && !description && 'mb-6')}>{title}</h2>}
          {attribution && (
            <div className="flex gap-3 mb-4">
              <div className="bg-primaryBlue text-white dark:text-secondary-900 rounded-md px-2 inline-block">
                {attribution.company}
              </div>
              {attribution.authors && (
                <span className="text-secondary-500 dark:text-secondary-300">{attribution.authors.join(', ')}</span>
              )}
            </div>
          )}
          {description && <p className={cx(!newsfeed && 'line-clamp-3', 'mb-6 text-lg')}>{description}</p>}
          {cta && (
            <div className="absolute bottom-6 right-6 left-6 border-t border-secondary-100">
              <div className="grid justify-items-end pt-6">
                <CtaLink asButton style={buttonStyles.btnArrow} className="text-right">
                  {cta}
                </CtaLink>
              </div>
            </div>
          )}
        </div>
      </article>
    </LinkComponent>
  )
}
